// @flow
import { of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { putEmailApi, postPhoneApi, postPhoneVerifyApi } from './api';
import { setNewAlert } from '../../alert/duck';
import { actions as AuthActions } from '../../auth/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error';
import type { User } from '../../../entities';

export type SecurityAction = {
  +type: string;
  +email?: string;
  +prefix?: string;
  +phone?: string;
  +enableSmsNotifications?: boolean;
  +user?: User;
  +code?: string;
  +error?: HTTPError;
};

export type SecurityState = {
  +editSuccessful?: boolean;
  +isPuttingEmail?: boolean;
  +putEmailError?: HTTPError;
  +isPostingPhone?: boolean;
  +postPhoneError?: HTTPError;
  +isPostingCode?: boolean;
  +successfulVerification?: boolean;
  +postCodeError?: HTTPError;
};

// Actions
const SECURITY_PUT_EMAIL_REQUEST = 'SECURITY_PUT_EMAIL_REQUEST';
const SECURITY_PUT_EMAIL_SUCCESS = 'SECURITY_PUT_EMAIL_SUCCESS';
const SECURITY_PUT_EMAIL_ERROR = 'SECURITY_PUT_EMAIL_ERROR';
const SECURITY_POST_PHONE_REQUEST = 'SECURITY_POST_PHONE_REQUEST';
const SECURITY_POST_PHONE_SUCCESS = 'SECURITY_POST_PHONE_SUCCESS';
const SECURITY_POST_PHONE_ERROR = 'SECURITY_POST_PHONE_ERROR';
const SECURITY_POST_CODE_REQUEST = 'SECURITY_POST_CODE_REQUEST';
const SECURITY_POST_CODE_SUCCESS = 'SECURITY_POST_CODE_SUCCESS';
const SECURITY_POST_CODE_ERROR = 'SECURITY_POST_CODE_ERROR';

// Action creators
export function editEmail(email: string): SecurityAction {
  return {
    type: SECURITY_PUT_EMAIL_REQUEST,
    email,
  }
}

export function editEmailSuccess(): SecurityAction {
  return {
    type: SECURITY_PUT_EMAIL_SUCCESS,
  }
}

export function editEmailError(error: HTTPError): SecurityAction {
  return {
    type: SECURITY_PUT_EMAIL_ERROR,
    error,
  }
}

export function postPhone(phone: string, prefix: string, enableSmsNotifications: boolean): SecurityAction {
  return {
    type: SECURITY_POST_PHONE_REQUEST,
    phone,
    prefix,
    enableSmsNotifications,
  }
}

export function postPhoneSuccess(): SecurityAction {
  return {
    type: SECURITY_POST_PHONE_SUCCESS,
  }
}

export function postPhoneErrorFn(error: HTTPError): SecurityAction {
  return {
    type: SECURITY_POST_PHONE_ERROR,
    error,
  }
}

export function postCode(code: string): SecurityAction {
  return {
    type: SECURITY_POST_CODE_REQUEST,
    code,
  };
}

export function postCodeSuccess(): SecurityAction {
  return {
    type: SECURITY_POST_CODE_SUCCESS,

  }
}

export function postCodeErrorFn(error: HTTPError): SecurityAction {
  return {
    type: SECURITY_POST_CODE_ERROR,
    error,
  };
}

export const actions = {
  SECURITY_PUT_EMAIL_REQUEST,
  SECURITY_PUT_EMAIL_SUCCESS,
  SECURITY_PUT_EMAIL_ERROR,
  SECURITY_POST_PHONE_REQUEST,
  SECURITY_POST_PHONE_SUCCESS,
  SECURITY_POST_PHONE_ERROR,
  SECURITY_POST_CODE_REQUEST,
  SECURITY_POST_CODE_SUCCESS,
  SECURITY_POST_CODE_ERROR,
  editEmail,
  editEmailSuccess,
  editEmailError,
  postPhone,
  postPhoneSuccess,
  postPhoneErrorFn,
  postCode,
  postCodeSuccess,
  postCodeErrorFn,
};

export const initialState: SecurityState = {};

// Reducer
export default function reducer(state: SecurityState = initialState,
                                action: SecurityAction): SecurityState {
  switch (action.type) {
    case SECURITY_PUT_EMAIL_REQUEST:
      return {
        isPuttingEmail: true,
      };
    case SECURITY_PUT_EMAIL_SUCCESS:
      return {
        editSuccessful: true,
      };
    case SECURITY_PUT_EMAIL_ERROR:
      return {
        putEmailError: action.error,
      };
    case SECURITY_POST_PHONE_REQUEST:
      return {
        isPostingPhone: true,
      };
    case SECURITY_POST_PHONE_SUCCESS:
      return {
        editSuccessful: true,
      };
    case SECURITY_POST_PHONE_ERROR:
      return {
        postPhoneError: action.error,
      };
    case SECURITY_POST_CODE_REQUEST:
      return {
        isPostingCode: true,
      };
    case SECURITY_POST_CODE_SUCCESS:
      return {
        successfulVerification: true,
      };
    case SECURITY_POST_CODE_ERROR:
      return {
        postCodeError: action.error,
      };
    default:
      return state;
  }
}

// Epics
export function editEmailEpic(action$: Observable<SecurityAction>): Observable<SecurityAction> {
  return action$.pipe(
    ofType(SECURITY_PUT_EMAIL_REQUEST),
    mergeMap(({ email }) => putEmailApi({ email }).pipe(
      mergeMap(() => of(editEmailSuccess(), AuthActions.updateUser({ email }))),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(editEmailError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(editEmailError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(editEmailError(e));
      }))
    ));
}

export function postPhoneEpic(action$: Observable<SecurityAction>): Observable<SecurityAction> {
  return action$.pipe(
    ofType(SECURITY_POST_PHONE_REQUEST),
    mergeMap(({ phone, prefix, enableSmsNotifications }) =>
      postPhoneApi({ phone, phoneCountry: prefix, enableSmsNotifications }).pipe(
        mergeMap((user: User) =>
          of(postPhoneSuccess(), AuthActions.updateUser({ phone, phoneCountry: user.phoneCountry }))),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postPhoneErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postPhoneErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postPhoneErrorFn(e));
        }))
    ));
}

export function postCodeEpic(action$: Observable<SecurityAction>): Observable<SecurityAction> {
  return action$.pipe(
    ofType(SECURITY_POST_CODE_REQUEST),
    mergeMap(({ code }) =>
      postPhoneVerifyApi({ token: code }).pipe(
        mergeMap((user: User) =>
          of(postCodeSuccess(), AuthActions.updateUser({ isPhoneVerified: user.isPhoneVerified }))),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postCodeErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            if (e.data && e.data.name === 'VerificationTokenError') {
              return of(postCodeErrorFn(e));
            }
            return of(postCodeErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postCodeErrorFn(e));
        }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/preferences/security/duck.js